import { useState } from "react";
import { ArrowUp, ArrowDown, Palette, Text, List, Save, Moon, Sun, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/components/theme-provider";

// Demo menu features
const initialMenu = [
  "Business/Investments",
  "Profit",
  "Expenses",
  "Products/Services List",
  "Customer Database",
  "Supplier Database",
  "Profit Projector",
  "New Investment Calculator",
];

const colorSchemes = [
  { label: "Classic", colors: ["#2563eb", "#14b8a6", "#f59e42", "#c026d3"] },
  { label: "Pastel", colors: ["#7dd3fc", "#a7f3d0", "#f9a8d4", "#fde68a"] },
  { label: "Mono", colors: ["#334155", "#475569", "#64748b", "#94a3b8"] },
];

const textSizes = [
  { label: "Small", className: "text-sm" },
  { label: "Medium", className: "text-base" },
  { label: "Large", className: "text-lg" },
];

const Settings = () => {
  const [menu, setMenu] = useState(initialMenu);
  const [textSize, setTextSize] = useState("Medium");
  const [color, setColor] = useState("Classic");
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Reorder menu helpers
  const move = (idx: number, dir: number) => {
    if ((idx === 0 && dir === -1) || (idx === menu.length - 1 && dir === 1)) return;
    const m = [...menu];
    const [item] = m.splice(idx, 1);
    m.splice(idx + dir, 0, item);
    setMenu(m);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start py-20 bg-background dark:bg-background px-4">
      {/* Home button */}
      <button
        onClick={() => navigate("/")}
        className="absolute left-4 top-4 bg-white dark:bg-card rounded-full p-2 shadow hover:bg-primary/10 text-primary transition z-20 border"
      >
        <Home size={22} />
      </button>
      <div className="mx-auto max-w-xl w-full bg-card shadow-lg rounded-2xl p-8">
        <h1 className="text-2xl font-bold mb-6 flex items-center gap-2"><List /> Settings</h1>

        {/* Dark Mode Section */}
        <div className="mb-7">
          <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
            {theme === 'dark' ? <Moon className="text-blue-400" /> : <Sun className="text-orange-500" />}
            Appearance
          </h2>
          <div className="bg-muted/30 rounded-lg p-4 space-y-3">
            <p className="text-sm text-muted-foreground">Choose your preferred theme for VentureTrack</p>
            <div className="flex gap-3">
              <button
                onClick={() => setTheme('light')}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border font-medium transition-all ${
                  theme === 'light' 
                    ? "bg-primary text-primary-foreground border-primary shadow-sm" 
                    : "bg-background border-border text-foreground hover:bg-muted hover:border-primary/50"
                }`}
              >
                <Sun size={18} className={theme === 'light' ? 'text-primary-foreground' : 'text-orange-500'} />
                Light Mode
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border font-medium transition-all ${
                  theme === 'dark' 
                    ? "bg-primary text-primary-foreground border-primary shadow-sm" 
                    : "bg-background border-border text-foreground hover:bg-muted hover:border-primary/50"
                }`}
              >
                <Moon size={18} className={theme === 'dark' ? 'text-primary-foreground' : 'text-blue-400'} />
                Dark Mode
              </button>
              <button
                onClick={() => setTheme('system')}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border font-medium transition-all ${
                  theme === 'system' 
                    ? "bg-primary text-primary-foreground border-primary shadow-sm" 
                    : "bg-background border-border text-foreground hover:bg-muted hover:border-primary/50"
                }`}
              >
                <div className="flex">
                  <Sun size={9} className="text-orange-500" />
                  <Moon size={9} className="text-blue-400" />
                </div>
                System
              </button>
            </div>
            <div className="text-xs text-muted-foreground">
              {theme === 'system' && "Follows your device's theme preference"}
              {theme === 'light' && "Always use light theme"}
              {theme === 'dark' && "Always use dark theme"}
            </div>
          </div>
        </div>
        <div className="mb-7">
          <h2 className="font-semibold text-lg mb-2 flex items-center gap-2"><Text /> Text Size</h2>
          <div className="flex gap-3">
            {textSizes.map(opt => (
              <button
                key={opt.label}
                className={`px-4 py-2 rounded-lg border font-medium transition-all ${textSize === opt.label ? "bg-muted text-primary border-primary" : "bg-background border-border text-muted-foreground hover:bg-muted"}`}
                onClick={() => setTextSize(opt.label)}
              >{opt.label}</button>
            ))}
          </div>
        </div>
        <div className="mb-7">
          <h2 className="font-semibold text-lg mb-2 flex items-center gap-2"><Palette /> Chart Color Scheme</h2>
          <div className="flex gap-5 flex-wrap items-center">
            {colorSchemes.map(cs => (
              <button
                key={cs.label}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg border transition-all ${color === cs.label ? "border-primary bg-muted" : "border-border bg-background hover:bg-muted"}`}
                onClick={() => setColor(cs.label)}
              >
                <span>{cs.label}</span>
                <div className="flex gap-0.5 ml-2">
                  {cs.colors.map(c => <span key={c} className="w-5 h-5 inline-block rounded" style={{ background: c }} />)}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-semibold text-lg mb-2 flex items-center gap-2"><List /> Menu Order</h2>
          <div className="rounded-lg bg-background p-3 border border-border divide-y divide-border mb-2">
            {menu.map((item, i) => (
              <div key={item} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0 justify-between">
                <span>{item}</span>
                <div className="flex gap-1">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1 rounded hover:bg-muted disabled:opacity-50"><ArrowUp size={18}/></button>
                  <button disabled={i === menu.length-1} onClick={() => move(i, 1)} className="p-1 rounded hover:bg-muted disabled:opacity-50"><ArrowDown size={18}/></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex mt-6 justify-end">
          <button className="bg-primary text-primary-foreground rounded-lg px-6 py-2 font-semibold flex items-center gap-2 hover:bg-primary/90 transition"><Save size={18}/> Save</button>
        </div>
      </div>
    </div>
  );
};

export default Settings;